import React, { useEffect, useState } from 'react';
import { teacherApi } from '../../services/api';

const F = { fontFamily: 'Inter' } as const;

interface Institution {
  id: number | string;
  name: string;
  logoUrl?: string | null;
  logo_url?: string | null;
}

interface ClassInfo {
  id: string;
  name: string;
  students: number;
}

const TeacherInstitutions: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  // Detail modal
  const [detailInst, setDetailInst] = useState<Institution | null>(null);
  const [detailClasses, setDetailClasses] = useState<ClassInfo[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Kurum kodu redeem (#kurum-kodu)
  const [codeInput, setCodeInput] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [redeemSuccess, setRedeemSuccess] = useState<string | null>(null);
  const [myCodes, setMyCodes] = useState<any[]>([]);

  const reloadInstitutions = () => {
    setLoading(true);
    Promise.all([
      teacherApi.institutions(),
      teacherApi.myInstitutionCodes(),
    ])
      .then(([inst, codes]) => {
        setInstitutions(inst.data || []);
        setMyCodes(codes.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { reloadInstitutions(); }, []);

  const submitRedeem = async () => {
    setRedeemError(null);
    setRedeemSuccess(null);
    const code = codeInput.trim().toUpperCase();
    if (!code) { setRedeemError('Kod gerekli'); return; }
    setRedeeming(true);
    try {
      const r = await teacherApi.redeemInstitutionCode(code);
      setRedeemSuccess(
        `"${r.data.institution.name}" eklendi.${r.data.package ? ` Paket: ${r.data.package.name}` : ''}`
      );
      setCodeInput('');
      reloadInstitutions();
      setTimeout(() => { setShowAddModal(false); setRedeemSuccess(null); }, 2200);
    } catch (e: any) {
      setRedeemError(e?.response?.data?.error ?? e.message ?? 'Kod kullanılamadı');
    } finally {
      setRedeeming(false);
    }
  };

  const openDetail = async (inst: Institution) => {
    setDetailInst(inst);
    setLoadingDetail(true);
    try {
      const res = await teacherApi.classes();
      const allClasses: any[] = res.data || [];
      // Filter classes that belong to this institution
      const matched = allClasses
        .filter((c: any) => c.campuses?.institutions?.id === inst.id || c.campuses?.institutions?.name === inst.name)
        .map((c: any) => ({
          id: String(c.id),
          name: c.name,
          students: c.class_student_enrollments?.length || 0,
        }));
      setDetailClasses(matched);
    } catch {
      setDetailClasses([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Kurumlarım</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:opacity-90 transition-opacity"
          style={{ background: '#e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#fafafa" strokeWidth="3" strokeLinecap="round">
            <line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" />
          </svg>
          <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Okul Ekle</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : (() => {
        // Merged list: hem teacher_subjects'ten gelen kurumlar hem redeem edilmiş kodlar.
        const merged = new Map<number | string, Institution>();
        institutions.forEach((i) => merged.set(i.id, i));
        myCodes.forEach((c: any) => {
          if (c.institution && !merged.has(c.institution.id)) {
            merged.set(c.institution.id, { id: c.institution.id, name: c.institution.name });
          }
        });
        const list = Array.from(merged.values());
        if (list.length === 0) {
          return <p className="text-center text-gray-400 py-16">Henüz bağlı olduğunuz bir kurum yok. "+ Okul Ekle" ile kurum kodunuzu girerek başlayın.</p>;
        }
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {list.map((inst) => {
              const linkedPackages = myCodes.filter((c: any) => c.institution?.id === inst.id && c.package);
              return (
                <div key={inst.id}
                  className="bg-[#fafafa] rounded-[18px] overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                  style={{ boxShadow: '0px 1.66px 6.63px rgba(0,0,0,0.25)' }}
                  onClick={() => openDetail(inst)}>
                  <div className="h-[100px] bg-[#3973bf] flex items-center justify-center">
                    <img src={inst.logoUrl || inst.logo_url || '/a-kurum-logo.png'} alt="" className="h-[50px] object-contain opacity-90" />
                  </div>
                  <div className="p-3">
                    <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#2b2b2b' }} className="truncate">{inst.name}</p>
                    {linkedPackages.length > 0 && (
                      <p style={{ ...F, fontSize: 11, color: '#3973bf', fontWeight: 600 }} className="mt-1">
                        🪙 {linkedPackages.length} paket
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── Institution Detail Modal ── */}
      {detailInst && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setDetailInst(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[560px] p-7"
            style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
            <button onClick={() => setDetailInst(null)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-200 flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>

            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 rounded-[12px] bg-[#3973bf] flex items-center justify-center flex-shrink-0">
                <img src={detailInst.logoUrl || detailInst.logo_url || '/a-kurum-logo.png'} alt="" className="h-8 object-contain opacity-90" />
              </div>
              <div>
                <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b' }}>{detailInst.name}</h2>
                <p style={{ ...F, fontSize: 14, color: '#3973bf' }}>Öğretmen</p>
              </div>
            </div>

            <div className="h-[1px] bg-gray-200 mb-4" />

            {/* Bağlı paket(ler) — myCodes'tan bu kuruma bağlı paket info'larını filtrele */}
            {(() => {
              const codesForInst = myCodes.filter((c: any) => c.institution?.id === detailInst.id && c.package);
              if (codesForInst.length === 0) return null;
              return (
                <div className="mb-5">
                  <p style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b', marginBottom: 8 }}>
                    Bağlı Paketler ({codesForInst.length})
                  </p>
                  <div className="space-y-2">
                    {codesForInst.map((c: any) => {
                      const p = c.package;
                      const pctStu = p.max_students > 0 ? (p.used_students / p.max_students) * 100 : 0;
                      const pctCre = p.max_credits > 0 ? (p.used_credits / p.max_credits) * 100 : 0;
                      const statusBg = p.status === 'active' ? '#dff5e0' : p.status === 'exhausted' ? '#fdecef' : '#f0f0f0';
                      const statusFg = p.status === 'active' ? '#1f7a35' : p.status === 'exhausted' ? '#c73d74' : '#666';
                      return (
                        <div key={c.id} className="bg-white rounded-[12px] p-3 border border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#2b2b2b' }}>{p.name}</span>
                            <span style={{ background: statusBg, color: statusFg, ...F, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>
                              {p.status === 'active' ? 'AKTİF' : p.status === 'exhausted' ? 'DOLDU' : 'İPTAL'}
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            <div>
                              <div className="flex justify-between text-[11px] mb-0.5" style={F}>
                                <span style={{ color: '#888' }}>Öğrenci</span>
                                <span style={{ color: '#2b2b2b', fontWeight: 700 }}>{p.used_students} / {p.max_students}</span>
                              </div>
                              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#3973bf]" style={{ width: `${Math.min(100, pctStu)}%` }} />
                              </div>
                            </div>
                            <div>
                              <div className="flex justify-between text-[11px] mb-0.5" style={F}>
                                <span style={{ color: '#888' }}>Kredi</span>
                                <span style={{ color: '#2b2b2b', fontWeight: 700 }}>{p.used_credits} / {p.max_credits}</span>
                              </div>
                              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#e5599b]" style={{ width: `${Math.min(100, pctCre)}%` }} />
                              </div>
                            </div>
                            <p style={{ ...F, fontSize: 10, color: '#888' }}>
                              Kod: <span className="font-mono">{c.code}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="h-[1px] bg-gray-200 mt-4 mb-4" />
                </div>
              );
            })()}

            <p style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b', marginBottom: 12 }}>
              Bu kurumda atandığım sınıflar {loadingDetail ? '' : `(${detailClasses.length})`}
            </p>

            {loadingDetail ? (
              <div className="flex justify-center py-6"><div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
            ) : detailClasses.length === 0 ? (
              <p style={{ ...F, fontSize: 14, color: '#888' }}>Bu kuruma ait sınıf bulunamadı.</p>
            ) : (
              <div className="space-y-2 max-h-[240px] overflow-y-auto">
                {detailClasses.map((c) => (
                  <div key={c.id} className="bg-white rounded-[12px] px-4 py-3 flex items-center justify-between border border-gray-100">
                    <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>{c.name}</p>
                    <div className="flex items-center gap-1.5">
                      <svg width="16" height="13" viewBox="0 0 24 20" fill="#888"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" /></svg>
                      <span style={{ ...F, fontSize: 13, color: '#888' }}>{c.students} öğrenci</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button onClick={() => setDetailInst(null)}
              className="mt-5 w-full rounded-[12px] py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
              <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Kapat</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Add School Modal ── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[860px] p-10 overflow-hidden">
            <button onClick={() => setShowAddModal(false)}
              className="absolute top-5 right-5 w-[48px] h-[48px] rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="4" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>
            <div className="flex gap-8">
              <div className="flex-1">
                <div className="mb-6">
                  <label style={{ ...F, fontSize: 24, fontWeight: 500, color: '#2b2b2b', display: 'block', marginBottom: 10 }}>Okul Kodunu Giriniz</label>
                  <div className="flex items-center bg-[#fafafa] rounded-[20px] px-4 h-[70px] gap-4" style={{ border: '2px solid #e5599b' }}>
                    <div className="w-[48px] h-[48px] rounded-[16px] bg-[#e5599b] flex items-center justify-center flex-shrink-0">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fafafa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.78 7.78 5.5 5.5 0 017.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                      </svg>
                    </div>
                    <input
                      placeholder="KRM-XXXXXX-XXXX"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !redeeming) submitRedeem(); }}
                      disabled={redeeming}
                      className="flex-1 bg-transparent outline-none text-[20px] font-medium text-[#2b2b2b]"
                      style={{ ...F, letterSpacing: 1 }}
                    />
                  </div>
                  <p style={{ ...F, fontSize: 12, color: '#888', marginTop: 8 }}>
                    Kurumdan size verilen erişim kodunu girin. Kuruma bağlı paket de hesabınıza eklenir.
                  </p>
                </div>
                {redeemError && (
                  <div className="mb-3 rounded-[10px] px-4 py-2 text-[14px]" style={{ background: '#fdecef', color: '#c73d74' }}>
                    {redeemError}
                  </div>
                )}
                {redeemSuccess && (
                  <div className="mb-3 rounded-[10px] px-4 py-2 text-[14px]" style={{ background: '#dff5e0', color: '#1f7a35' }}>
                    {redeemSuccess}
                  </div>
                )}
                <button
                  onClick={submitRedeem}
                  disabled={redeeming || !codeInput.trim()}
                  className="rounded-[12px] py-4 hover:opacity-90 transition-opacity w-full disabled:opacity-50"
                  style={{ background: '#e5599b' }}
                >
                  <span style={{ ...F, fontSize: 18, fontWeight: 700, color: '#fafafa' }}>
                    {redeeming ? 'Kontrol ediliyor…' : 'Okul Ekle'}
                  </span>
                </button>
              </div>
              <img src="/a-popup-mascot-class.png" alt="" className="hidden lg:block w-[220px] h-[220px] object-contain self-center flex-shrink-0" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherInstitutions;
